import { AccessTimeFilled, Fastfood, FindInPage, Help, Home, Payments, ShoppingCart, Store } from '@mui/icons-material';
import HelpScreen from 'screens/content/HelpScreen';
import OrderHistoryScreen from 'screens/content/OrderHistoryScreen';
import Auth from 'screens/Auth';
import App from 'screens/App';
import OrdersScreen from 'screens/content/OrdersScreen';
import PacksScreen from 'screens/content/PacksScreen';
import ReviewsScreen from 'screens/content/ReviewsScreen';
import StoreScreen from 'screens/content/StoreScreen';
import { Navigate, Route, RouteProps, createRoutesFromElements } from 'react-router-dom';
import StoreImagesScreen from 'screens/content/StoreImagesScreen';
import OpeningHoursScreen from 'screens/content/OpeningHoursScreen';
import ErrorScreen from 'components/Error';
import Content from 'screens/Content';
import MercadoPagoRedirectScreen from 'screens/content/MercadoPagoRedirectScreen';
import LoginScreen from 'screens/auth/LoginScreen';
import ImpersonateScreen from 'screens/auth/ImpersonateScreen';
import ResetPasswordScreen from 'screens/auth/ResetPasswordScreen';
import ResetPasswordConfirmScreen from 'screens/auth/ResetPasswordConfirmScreen';
import Error from 'components/Error';
import OrganizationPacksScreen from 'screens/content/OrganizationPacksScreen';
import NotificationsScreen from 'screens/content/NotificationsScreen';
import BillingScreen from 'screens/content/BillingScreen';
import { Permission } from 'consts';
import DashboardScreen from 'screens/content/DashboardScreen';
import ReceiptsAndCreditNotesScreen from 'screens/content/ReceiptsAndCreditNotesScreen';

export const CURRENT_ORDERS_PATH = 'orders';

// Note: only screens defined in the top level will be shown in the left menu
// Screens inside the children array need to be navigatable from their parent
export const SCREENS: Screen[] = [
  {
    name: 'Inicio',
    icon: <Home color='primary' />,
    content: <DashboardScreen />,
    path: 'home',
  },
  {
    name: 'Packs',
    icon: <Fastfood color='primary' />,
    content: <PacksScreen />,
    path: 'packs',
    children: [
      {
        name: 'Imágenes',
        path: 'images',
        content: <StoreImagesScreen />,
      },
      {
        name: 'Packs compartidos',
        path: 'shared',
        content: <OrganizationPacksScreen />,
      },
    ],
  },
  {
    name: 'Pedidos actuales',
    icon: <ShoppingCart color='primary' />,
    content: <OrdersScreen />,
    path: CURRENT_ORDERS_PATH,
  },
  {
    name: 'Historial de pedidos',
    icon: <FindInPage color='primary' />,
    content: <OrderHistoryScreen />,
    path: 'order-history',
  },
  {
    name: 'Tu tienda',
    icon: <Store color='primary' />,
    content: <StoreScreen />,
    path: 'store',
    children: [
      {
        name: 'Reseñas',
        path: 'reviews',
        content: <ReviewsScreen />,
      },
    ],
  },
  {
    name: 'Horarios',
    path: 'times',
    icon: <AccessTimeFilled color='primary' />,
    content: <OpeningHoursScreen />,
  },
  {
    name: 'Facturación',
    path: 'billing',
    icon: <Payments color='primary' />,
    content: <BillingScreen />,
    permission: Permission.BILLING,
    children: [
      {
        name: 'Recibos y notas de crédito',
        path: 'receipts',
        content: <ReceiptsAndCreditNotesScreen />,
      },
    ],
  },
  {
    name: 'Ayuda',
    icon: <Help color='primary' />,
    content: <HelpScreen />,
    path: 'help',
  },
  {
    name: 'Notificaciones',
    content: <NotificationsScreen />,
    path: 'notifications',
    notShownInDrawer: true,
  },
];

export type Screen = {
  name: string;
  content: JSX.Element;
  path: string;
  icon?: JSX.Element; // Only needs to be defined for top-level (left menu) screens
  children?: Screen[];
  notShownInDrawer?: boolean; // Set true for screens that aren't nested inside other ones but aren't shown in the left menu either
  permission?: Permission;
};

export type RouterScreen = {
  breadCrumbs: string[];
  content: JSX.Element;
  path: string;
  parentPath?: string;
};

export let ROUTER_SCREENS: RouterScreen[] = [];
function flattenScreens(screens: Screen[], parent?: RouterScreen) {
  screens.forEach((screen) => {
    const path = (parent ? parent.path + '/' : '') + screen.path;
    const routerScreen = {
      breadCrumbs: parent ? parent.breadCrumbs.concat(screen.name) : [screen.name],
      content: screen.content,
      path: path,
      parentPath: parent?.path,
    };
    ROUTER_SCREENS.push(routerScreen);
    screen.children && flattenScreens(screen.children, routerScreen);
  });
}
flattenScreens(SCREENS);

// default props to apply to all routes
const props: RouteProps = { errorElement: <Error /> };

export const ROUTES = createRoutesFromElements(
  <Route {...props} path='*' element={<App />}>
    <Route {...props} path='app' element={<Content />}>
      {ROUTER_SCREENS.map((item) => (
        <Route {...props} path={item.path} key={item.path} element={item.content} />
      ))}
      <Route {...props} path='error' element={<ErrorScreen />} />
      <Route {...props} path='mercadopago/failure' element={<MercadoPagoRedirectScreen success={false} />} />
      <Route {...props} path='mercadopago/success' element={<MercadoPagoRedirectScreen success={true} />} />
      <Route {...props} path='' element={<Navigate to='home' />} />
      <Route {...props} path='*' element={<Navigate to='home' />} />
    </Route>
    <Route {...props} path='auth' element={<Auth />}>
      <Route {...props} path='login' element={<LoginScreen />} />
      <Route {...props} path='impersonate/:token/:storeId?' element={<ImpersonateScreen />} />
      <Route {...props} path='reset-password' element={<ResetPasswordScreen />} />
      <Route {...props} path='reset-password-confirm' element={<ResetPasswordConfirmScreen />} />
      <Route {...props} path='' element={<Navigate to='login' />} />
      <Route {...props} path='*' element={<Navigate to='login' />} />
    </Route>
    <Route {...props} path='*' element={<Navigate to='app' replace={true} />} />
  </Route>,
);
